/**
 * Guided View Share Links Utility v2.3
 * 
 * Generates and validates signed, time-bound shareable demo links.
 * 
 * Link Parameters:
 * - persona: rbi | bank | client
 * - mode: auto | manual
 * - startStep: 0-9 (optional, default 0)
 * - autoplay: true | false
 * - expiresAt: timestamp
 * - signature: HMAC signature for validation
 */

// Simple hash function for client-side signature (in production, use backend signing)
const simpleHash = (str) => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash;
  }
  return Math.abs(hash).toString(36);
};

// Secret key (in production, this would be server-side only)
const DEMO_LINK_SECRET = 'granullo-demo-v23-secret';

/**
 * Generate a signed demo link
 */
export const generateDemoLink = ({
  persona = 'client',
  mode = 'manual',
  startStep = 0,
  autoplay = false,
  expiresInHours = 24,
  createdBy = 'unknown'
}) => {
  const expiresAt = Date.now() + (expiresInHours * 60 * 60 * 1000);
  
  const payload = {
    p: persona,
    m: mode,
    s: startStep,
    a: autoplay ? '1' : '0',
    e: expiresAt,
    c: createdBy
  };
  
  // Create signature
  const dataString = `${payload.p}|${payload.m}|${payload.s}|${payload.a}|${payload.e}|${DEMO_LINK_SECRET}`;
  const signature = simpleHash(dataString);
  payload.sig = signature;
  
  // Encode as base64
  const encoded = btoa(JSON.stringify(payload));
  
  return {
    token: encoded,
    expiresAt: new Date(expiresAt),
    params: payload
  };
};

/**
 * Validate and decode a demo link token
 */
export const validateDemoLink = (token) => {
  try {
    const decoded = JSON.parse(atob(token));
    
    // Check expiry
    if (decoded.e < Date.now()) {
      return { valid: false, error: 'Link has expired', expired: true };
    }
    
    // Validate signature
    const dataString = `${decoded.p}|${decoded.m}|${decoded.s}|${decoded.a}|${decoded.e}|${DEMO_LINK_SECRET}`;
    const expectedSig = simpleHash(dataString);
    
    if (decoded.sig !== expectedSig) {
      return { valid: false, error: 'Invalid signature' };
    }
    
    return {
      valid: true,
      persona: decoded.p,
      mode: decoded.m,
      startStep: parseInt(decoded.s, 10),
      autoplay: decoded.a === '1',
      expiresAt: new Date(decoded.e),
      createdBy: decoded.c
    };
  } catch (e) {
    return { valid: false, error: 'Invalid link format' };
  }
};

/**
 * Build full shareable URL
 */
export const buildShareableUrl = (baseUrl, token) => {
  return `${baseUrl}/demo?token=${encodeURIComponent(token)}`;
};

/**
 * Parse demo token from URL
 */
export const parseDemoTokenFromUrl = () => {
  const params = new URLSearchParams(window.location.search);
  return params.get('token');
};

/**
 * Check if current URL is a demo link
 */
export const isDemoLinkUrl = () => {
  return window.location.pathname === '/demo' && parseDemoTokenFromUrl() !== null;
};

export default {
  generateDemoLink,
  validateDemoLink,
  buildShareableUrl,
  parseDemoTokenFromUrl,
  isDemoLinkUrl
};
